import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import {
  type IParamsCreateExternalWebhooks,
  type IParamsEventsPreview,
  type IResponseEventsPreview,
  type IParamsUpdateExternalWebhooks,
} from 'entities/Webhook'
import { WebhookApi } from 'entities/Webhook/api/webhook'
import { IntegrationsRoutes } from 'entities/ExportRouters'
import { type IntegrationWebhooksStore } from 'pages/settings/pages/integrations/pages/Webhooks/store/IntegrationWebhooksStore'

export class WebhookPageStore {
  private _name = ''
  private _url = ''
  private _throttleLimit = 1
  private _events: string[] = []
  private _isSaving = false
  private _urlError = ''
  private _nameError = ''
  private _eventsError = false
  private _eventsPreview: IResponseEventsPreview | null = null
  private _webhookId: number | null = null
  private _isLoading = false

  constructor(private _webhooksStore: IntegrationWebhooksStore, webhookId?: string) {
    makeAutoObservable(this)

    this._webhookId = webhookId ? Number(webhookId) : null

    this.init()
  }

  init = async () => {
    if (!this._webhookId) return

    try {
      runInAction(() => {
        this._isLoading = true
      })

      const { data } = await WebhookApi.getExternalWebhookById(this._webhookId)

      runInAction(() => {
        this._name = data.name
        this._url = data.url
        this._throttleLimit = data.throttle_limit
        this._events = data.events
      })

      this.updateEventsPreview()
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._isLoading = false
      })
    }
  }

  setName = (name: string) => {
    this._name = name

    if (this._nameError) {
      this._nameError = ''
    }
  }

  setUrl = (url: string) => {
    this._url = url

    if (this._urlError) {
      this._urlError = ''
    }
  }

  setThrottleLimit = (limit: string) => {
    let numericValue = parseInt(limit) || 1

    if (numericValue < 1) {
      numericValue = 1
    } else if (numericValue > 100) {
      numericValue = 100
    }

    this._throttleLimit = numericValue
  }

  updateEventsPreview = async () => {
    try {
      const params: IParamsEventsPreview = {}
      this._events.forEach((event, index) => {
        params[`events[${index}]`] = event
      })

      const response = await WebhookApi.getEventsPreview(params)

      this._eventsPreview = response.data
    } catch (error) {
      logger.error(error)
    }
  }

  switchEvent = (event: string) => {
    this._eventsError = false

    this._events = this._events.includes(event)
      ? this._events.filter((e) => e !== event)
      : [...this._events, event]

    if (this._events.length) {
      this.updateEventsPreview()
    } else {
      this._eventsPreview = null
    }
  }

  switchAllEvent = (events: string[], value: boolean) => {
    this._eventsError = false

    if (value) {
      const newEvents = events.filter((event) => !this._events.includes(event))
      this._events = [...this._events, ...newEvents]
    } else {
      this._events = this._events.filter((e) => !events.includes(e))
    }

    if (this._events.length) {
      this.updateEventsPreview()

      return
    }

    this._eventsPreview = null
  }

  validateName = () => {
    if (!this._name.trim()) {
      this._nameError = 'The name field is required.'
      return false
    }
    if (this._name.trim().length < 3) {
      this._nameError = 'The name must be at least 3 characters.'
      return false
    }
    this._nameError = ''
    return true
  }

  validateUrl = () => {
    if (!this._url.trim()) {
      this._urlError = 'The URL field is required.'
      return false
    }
    if (!this._url.startsWith('https://')) {
      this._urlError = 'Please use a secure url that starts with https://'
      return false
    }
    if (!this.isValidUrl(this._url)) {
      this._urlError = 'Please enter a valid URL'
      return false
    }
    this._urlError = ''
    return true
  }

  validateEvents = () => {
    this._eventsError = Boolean(!this._events.length)

    return this._eventsError
  }

  createWebhook = async () => {
    const isNameValid = this.validateName()
    const isUrlValid = this.validateUrl()
    const isEventsError = this.validateEvents()

    if (!isNameValid || !isUrlValid || isEventsError) {
      return
    }

    try {
      runInAction(() => {
        this._isSaving = true
      })

      const params: IParamsCreateExternalWebhooks = {
        name: this._name.trim(),
        url: this._url.trim(),
        events: this._events,
        throttle_limit: this._throttleLimit,
      }

      await WebhookApi.createExternalWebhooks(params)

      this._webhooksStore.loadWebhooks()

      uiStore.changeRoute({
        path: `${IntegrationsRoutes.settings}/${IntegrationsRoutes.root}/${IntegrationsRoutes.webhooks}`,
      })

      showToast({
        type: 'success',
        title: 'Webhook created successfully',
      })
    } catch (error) {
      logger.error(error)
      showToast({
        type: 'error',
        title: 'Failed to create webhook. Please try again.',
      })
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  updateWebhook = async () => {
    const isNameValid = this.validateName()
    const isUrlValid = this.validateUrl()

    if (!isNameValid || !isUrlValid || !this._webhookId) {
      return
    }

    try {
      runInAction(() => {
        this._isSaving = true
      })

      const params: IParamsUpdateExternalWebhooks = {
        name: this._name.trim(),
        url: this._url.trim(),
        events: this._events,
        throttle_limit: this._throttleLimit,
      }

      const { data } = await WebhookApi.updateExternalWebhooks(this._webhookId, params)

      this._webhooksStore.updateWebhook(data)

      uiStore.changeRoute({
        path: `${IntegrationsRoutes.settings}/${IntegrationsRoutes.root}/${IntegrationsRoutes.webhooks}`,
      })

      showToast({
        type: 'success',
        title: 'Webhook was updated successfully',
      })
    } catch (error) {
      logger.error(error)
      showToast({
        type: 'error',
        title: 'Failed to update webhook. Please try again.',
      })
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  submitWebhook = () => {
    this._webhookId ? this.updateWebhook() : this.createWebhook()
  }

  private isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  get isLoading() {
    return this._isLoading
  }

  get name() {
    return this._name
  }

  get url() {
    return this._url
  }

  get throttleLimit() {
    return this._throttleLimit.toString()
  }

  get parsedEventsPreview() {
    if (!this._eventsPreview) return null

    const parsed: Record<string, string> = {}
    Object.entries(this._eventsPreview).forEach(([eventName, schemaString]) => {
      try {
        parsed[eventName] = JSON.parse(schemaString)
      } catch (error) {
        logger.error(error)
        parsed[eventName] = schemaString
      }
    })

    return parsed
  }

  get events() {
    return this._events
  }

  get isSaving() {
    return this._isSaving
  }

  get urlError() {
    return this._urlError
  }

  get nameError() {
    return this._nameError
  }

  get eventsError() {
    return this._eventsError
  }

  get webhookId() {
    return this._webhookId
  }
}
